/**
 * Waste Analysis Step Component
 * Phase 2 of new AI workflow - AI analyzes project description to generate waste streams
 * This is where AI provides value: technical analysis, not personal data
 */

import { useState } from 'react';
import type { WasteStream, ProjectInfo, ApplicantInfo, OperationalRequirements, ImprovementSuggestionsResponse } from '../../types';
import { apiClient } from '../../services/api';
import type { ProjectFactsData } from './ProjectFactsStep';

export interface WasteAnalysisData {
  description: string;
  selectedMaterials: string[];
  wasteStreams: WasteStream[];
  confidence?: 'high' | 'medium' | 'low';
  operationalRequirements?: OperationalRequirements;
  improvementSuggestions?: ImprovementSuggestionsResponse;
}

interface WasteAnalysisStepProps {
  projectFacts: ProjectFactsData;
  countyId?: string;
  initialData?: Partial<WasteAnalysisData>;
  onNext: (data: WasteAnalysisData) => void;
  onBack: () => void;
}

const MATERIAL_OPTIONS = [
  // Structural Materials (High Diversion: 95%+)
  { id: 'concrete', label: 'Concrete/Masonry', icon: '🧱' },
  { id: 'wood', label: 'Wood/Lumber', icon: '🪵' },
  { id: 'metal', label: 'Metal/Steel', icon: '🔩' },
  { id: 'asphalt', label: 'Asphalt Paving', icon: '🛣️' },

  // Building Materials (Variable Diversion)
  { id: 'drywall', label: 'Drywall/Gypsum', icon: '🎨' },
  { id: 'brick', label: 'Brick/Block', icon: '🧱' },
  { id: 'stucco', label: 'Stucco/Plaster', icon: '🏠' },

  // Roofing Materials
  { id: 'roofing', label: 'Roofing Shingles', icon: '🏘️' },
  { id: 'roofing_metal', label: 'Metal Roofing', icon: '⚙️' },
  { id: 'tile_roof', label: 'Tile/Clay Roofing', icon: '🔶' },

  // Finishing Materials
  { id: 'flooring', label: 'Flooring/Hardwood', icon: '🟫' },
  { id: 'carpet', label: 'Carpet/Padding', icon: '🧶' },
  { id: 'tile', label: 'Tile/Ceramics', icon: '🔲' },
  { id: 'cabinetry', label: 'Cabinets/Millwork', icon: '🚪' },

  // MEP (Mechanical/Electrical/Plumbing)
  { id: 'electrical', label: 'Electrical/Wiring', icon: '🔌' },
  { id: 'plumbing', label: 'Plumbing/Pipes', icon: '🚰' },
  { id: 'hvac', label: 'HVAC/Ductwork', icon: '❄️' },

  // Insulation & Glazing
  { id: 'insulation', label: 'Insulation', icon: '🧯' },
  { id: 'glass', label: 'Glass/Windows', icon: '🪟' },

  // Packaging & Other
  { id: 'cardboard', label: 'Cardboard/Packaging', icon: '📦' },
  { id: 'plastic', label: 'Plastic/Vinyl', icon: '♻️' },
  { id: 'soil', label: 'Soil/Dirt', icon: '🟤' },
  { id: 'green_waste', label: 'Green Waste/Vegetation', icon: '🪴' },
];

export function WasteAnalysisStep({
  projectFacts,
  countyId,
  initialData,
  onNext,
  onBack,
}: WasteAnalysisStepProps) {
  const [description, setDescription] = useState(initialData?.description || '');
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>(
    initialData?.selectedMaterials || []
  );
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const characterCount = description.length;
  const isOptimalLength = characterCount >= 100 && characterCount <= 500;

  const toggleMaterial = (materialId: string) => {
    setSelectedMaterials((prev) =>
      prev.includes(materialId)
        ? prev.filter((id) => id !== materialId)
        : [...prev, materialId]
    );
  };

  const handleAnalyze = async () => {
    if (description.length < 20) {
      setError('Please provide more detail about your project (at least 20 characters)');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      // Convert ProjectFactsData to ProjectInfo format expected by API
      const projectInfo: ProjectInfo = {
        project_name: projectFacts.projectName,
        project_address: projectFacts.projectAddress,
        project_type: projectFacts.projectType as ProjectInfo['project_type'],
        estimated_start_date: projectFacts.startDate,
        estimated_completion_date: projectFacts.completionDate,
        total_square_footage: projectFacts.squareFootage
          ? parseInt(projectFacts.squareFootage)
          : undefined,
      };

      const applicantInfo: ApplicantInfo = {
        contact_name: projectFacts.contactName,
        contact_email: projectFacts.contactEmail,
        contact_phone: projectFacts.contactPhone,
        company_name: projectFacts.companyName || undefined,
      };

      const response = await apiClient.post<{
        success: boolean;
        planData?: {
          project_info: ProjectInfo;
          applicant_info: ApplicantInfo;
          waste_streams: WasteStream[];
          operational_requirements?: OperationalRequirements;
        };
        improvement_suggestions?: ImprovementSuggestionsResponse;
        taskId: string;
        cost: number;
        error?: string;
        confidence?: 'high' | 'medium' | 'low';
      }>('/plan-llm/generate', {
        job_description: description,
        county_id: countyId,
        project_info: projectInfo,
        applicant_info: applicantInfo,
        selected_materials: selectedMaterials,
      });

      if (!response.success || !response.planData) {
        throw new Error(response.error || 'Failed to analyze waste streams');
      }

      onNext({
        description,
        selectedMaterials,
        wasteStreams: response.planData.waste_streams,
        confidence: response.confidence || 'medium',
        operationalRequirements: response.planData.operational_requirements,
        improvementSuggestions: response.improvement_suggestions,
      });
    } catch (err) {
      console.error('Failed to analyze waste:', err);
      setError(
        err instanceof Error ? err.message : 'Unable to analyze waste streams. Please try again.'
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-neutral-900">Waste Stream Analysis</h3>
          <span className="text-sm text-neutral-500">Step 2 of 3</span>
        </div>
        <p className="text-sm text-neutral-600">
          Now describe your project and we&apos;ll calculate the required waste management details
          for CalGreen compliance.
        </p>
      </div>

      {/* AI Capabilities Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          <div>
            <p className="text-sm font-semibold text-blue-900 mb-2">AI Assistant Will:</p>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Identify waste materials from your description</li>
              <li>Calculate estimated quantities based on {projectFacts.projectType}</li>
              <li>Determine required diversion rates</li>
              <li>Ensure CalGreen 5.408 compliance (65% minimum)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Project Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-neutral-700 mb-1">
          Describe Your Project <span className="text-red-600">*</span>
        </label>
        <p className="text-sm text-neutral-500 mb-2">
          Tell us about the work being done, materials being used or removed, and any special
          considerations.
        </p>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Example: Complete kitchen remodel including removal of old cabinets, countertops, and appliances. Installing new hardwood cabinets, quartz counters, and tile backsplash. Also updating electrical and plumbing."
          className="input-field w-full px-4 py-3 text-base border rounded-lg min-h-32"
          disabled={isAnalyzing}
        />
        <div className="flex justify-between items-center mt-2">
          <span
            className={`text-sm ${
              isOptimalLength
                ? 'text-accent-600 font-medium'
                : characterCount > 500
                ? 'text-red-600'
                : 'text-neutral-500'
            }`}
          >
            {characterCount}/500 characters
            {isOptimalLength && ' (optimal length)'}
            {characterCount > 500 && ' (too long - aim for 100-500)'}
          </span>
          {characterCount > 0 && characterCount < 100 && (
            <span className="text-sm text-neutral-500">
              {100 - characterCount} more for best results
            </span>
          )}
        </div>
      </div>

      {/* Material Selection */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Construction Materials (Optional)
        </label>
        <p className="text-sm text-neutral-500 mb-3">
          Help AI be more accurate by selecting materials you&apos;ll be working with
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {MATERIAL_OPTIONS.map((material) => (
            <button
              key={material.id}
              type="button"
              onClick={() => toggleMaterial(material.id)}
              className={`p-3 rounded-lg border-2 transition-colors text-left ${
                selectedMaterials.includes(material.id)
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedMaterials.includes(material.id)}
                  onChange={() => toggleMaterial(material.id)}
                  className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="text-xl">{material.icon}</span>
                <span className="text-sm font-medium text-neutral-900">{material.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">AI Analysis Failed</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                type="button"
                onClick={() => {
                  onNext({
                    description,
                    selectedMaterials,
                    wasteStreams: [], // Empty - user will add manually in review
                  });
                }}
                className="text-sm font-medium text-red-900 underline mt-2 hover:no-underline"
              >
                Skip AI and add waste streams manually in review step →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between pt-4 border-t border-neutral-200">
        <button
          type="button"
          onClick={onBack}
          disabled={isAnalyzing}
          className="btn-secondary sm:w-auto order-2 sm:order-1 flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <button
          type="button"
          onClick={handleAnalyze}
          disabled={isAnalyzing || description.length < 20}
          className="btn-primary flex-1 sm:flex-initial order-1 sm:order-2 flex items-center justify-center gap-2 min-w-[200px]"
        >
          {isAnalyzing ? (
            <>
              <svg
                className="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
              Analyzing...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Generate Waste Plan
            </>
          )}
        </button>
      </div>
    </div>
  );
}
